
import { X } from "lucide-react";
import { NewsletterFormHeader } from "@/components/newsletter/NewsletterFormHeader";
import { NewsletterFormContent } from "@/components/newsletter/NewsletterFormContent";
import { useNewsletterForm } from "@/components/newsletter/useNewsletterForm";
import { useState } from "react";
import { VerificationMessage } from "@/components/newsletter/VerificationMessage";

interface NewsletterFormProps {
  onSuccess: () => void;
  className?: string;
  standalone?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

export function NewsletterForm({ 
  onSuccess, 
  className = "", 
  standalone = false, 
  onClose, 
  isModal = false 
}: NewsletterFormProps) {
  const [verificationSent, setVerificationSent] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | undefined>(undefined);
  
  const {
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    acceptNewsletter,
    setAcceptNewsletter,
    isSubmitting,
    emailError,
    setEmailError,
    handleSubmit
  } = useNewsletterForm((submittedEmail, isAlreadySubscribed = false) => {
    console.log("Newsletter subscription callback triggered:", { submittedEmail, isAlreadySubscribed });
    setVerifiedEmail(submittedEmail);
    setAlreadySubscribed(isAlreadySubscribed);
    setVerificationSent(!isAlreadySubscribed); // Only set verification sent if not already subscribed
    document.body.style.overflow = ''; // Ensure scrolling is restored after subscription
  });

  const formContainerClasses = isModal 
    ? "relative bg-white rounded-lg p-8 max-w-md w-full" 
    : `${className} ${standalone ? 'max-w-md mx-auto bg-[#FCACF4] p-6 rounded-lg shadow-md' : ''}`;

  const handleModalClose = () => {
    if (onClose) {
      document.body.style.overflow = ''; // Restore scrolling
      onClose();
    }
  };
  
  const handleVerificationClose = () => {
    setVerificationSent(false);
    setAlreadySubscribed(false);
    if (onClose) {
      onClose();
    } else {
      onSuccess();
    }
  };

  return (
    <div className={isModal ? "fixed inset-0 z-[100] flex items-center justify-center bg-black/70" : ""}>
      <div className={formContainerClasses}>
        {isModal && onClose && (
          <button 
            onClick={handleModalClose} 
            className="absolute right-4 top-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        )}
        
        {verificationSent || alreadySubscribed ? (
          <VerificationMessage 
            isModal={isModal}
            onClose={handleVerificationClose}
            email={verifiedEmail}
            isAlreadySubscribed={alreadySubscribed}
          />
        ) : (
          <>
            <NewsletterFormHeader 
              standalone={standalone} 
              isModal={isModal} 
            />
            
            <NewsletterFormContent
              email={email}
              setEmail={setEmail}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              acceptNewsletter={acceptNewsletter}
              setAcceptNewsletter={setAcceptNewsletter}
              isSubmitting={isSubmitting}
              emailError={emailError}
              setEmailError={setEmailError}
              handleSubmit={handleSubmit}
              isModal={isModal}
              standalone={standalone}
            />
          </>
        )}
      </div>
    </div>
  );
}
